// src/lib/marketData.ts: market context indicators for the dashboard.
//
// These are read through the `market-data` Edge Function rather than fetched
// directly. fredgraph.csv sends no CORS headers, so a browser request to it
// throws "TypeError: Failed to fetch" (verified against the live endpoint).
import { supabase } from '../services/supabase';

export interface MarketPoint {
  date: string;
  value: number;
}

export type MarketPeriod = '1Y' | '3Y' | '5Y';
export const MARKET_PERIODS: MarketPeriod[] = ['1Y', '3Y', '5Y'];

export interface MarketIndicator {
  label: string;
  value: string;
  description: string;
  source: string;
  /** true when a rising value is the risk-off reading (VIX, credit spreads). */
  higherIsBad: boolean;
  direction: 'up' | 'down' | 'flat' | null;
  /** Move across the window actually covered: "+18.3%" or "+39bp". */
  yoyLabel: string | null;
  /**
   * The span the data really covers. FRED keeps only ~3 years of the HY spread
   * series, so on a 5Y request that card must not be captioned "5Y".
   */
  spanLabel: string | null;
  history: MarketPoint[];
  /** Value at the start of the window, drawn as the reference line. */
  startValue: number | null;
}

/** Shown when the function is unreachable, so the section never renders empty. */
const PLACEHOLDERS: MarketIndicator[] = [
  { label: 'S&P 500',   value: '--',   description: 'Public market benchmark',      source: '', higherIsBad: false, direction: null, yoyLabel: null, spanLabel: null, history: [], startValue: null },
  { label: 'VIX',       value: '--',   description: 'Volatility index',             source: '', higherIsBad: true,  direction: null, yoyLabel: null, spanLabel: null, history: [], startValue: null },
  { label: 'US 10Y',    value: '--%',  description: 'Treasury yield',               source: '', higherIsBad: false, direction: null, yoyLabel: null, spanLabel: null, history: [], startValue: null },
  { label: 'HY Spread', value: '--bp', description: 'US high yield credit spread',  source: '', higherIsBad: true,  direction: null, yoyLabel: null, spanLabel: null, history: [], startValue: null },
];

export async function fetchMarketContext(period: MarketPeriod = '1Y'): Promise<MarketIndicator[]> {
  try {
    const { data, error } = await supabase.functions.invoke('market-data', { body: { period } });
    if (error) throw error;
    if (!Array.isArray(data) || data.length === 0) throw new Error('Empty market data');
    return data as MarketIndicator[];
  } catch (e) {
    console.warn('Market context unavailable:', e);
    return PLACEHOLDERS;
  }
}

/**
 * Line colour. A rising VIX or credit spread is the risk-off reading and shows
 * red; a rising index or yield shows green. Flat and unknown stay navy.
 */
export function indicatorColour(
  ind: MarketIndicator,
  palette: { success: string; danger: string; navy: string },
): string {
  if (!ind.direction || ind.direction === 'flat') return palette.navy;
  const rising = ind.direction === 'up';
  const good = ind.higherIsBad ? !rising : rising;
  return good ? palette.success : palette.danger;
}
